import { useEffect, useMemo, useState } from 'react';
import type { Client, Contact } from '../../types/crm';
import { idsMatch } from '../../utils/crmHelpers';
import { AccountTypeahead } from '../accounts/AccountTypeahead';
import { Modal } from '../ui/Modal';

type Props = {
  open: boolean;
  busy?: boolean;
  clients: Client[];
  contacts: Contact[];
  clientId: string;
  contactId: string;
  requesterEmail?: string;
  onClose: () => void;
  onSave: (clientId: string, contactId: string) => Promise<void>;
};

function contactLabel(c: Contact): string {
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ');
  if (name && c.email) return `${name} · ${c.email}`;
  return name || c.email || 'Contact';
}

function matchContactByEmail(contacts: Contact[], clientId: string, email?: string): string {
  const needle = email?.trim().toLowerCase();
  if (!clientId || !needle) return '';
  const match = contacts.find(
    (c) => idsMatch(c.client_id, clientId) && c.email?.trim().toLowerCase() === needle,
  );
  return match ? String(match.id) : '';
}

export function TicketLinkAccountModal({
  open,
  busy,
  clients,
  contacts,
  clientId,
  contactId,
  requesterEmail,
  onClose,
  onSave,
}: Props) {
  const [draftClientId, setDraftClientId] = useState('');
  const [draftContactId, setDraftContactId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const nextClient = clientId ? String(clientId) : '';
    let nextContact = contactId ? String(contactId) : '';
    // If account is linked but contact is empty, pre-fill by requester email.
    if (nextClient && !nextContact) {
      nextContact = matchContactByEmail(contacts, nextClient, requesterEmail);
    }
    setDraftClientId(nextClient);
    setDraftContactId(nextContact);
    setError(null);
  }, [open, clientId, contactId, contacts, requesterEmail]);

  const contactsForClient = useMemo(
    () => contacts.filter((c) => idsMatch(c.client_id, draftClientId)),
    [contacts, draftClientId],
  );

  function handleAccountPick(nextClientId: string) {
    const id = nextClientId ? String(nextClientId) : '';
    setDraftClientId(id);
    if (!id) {
      setDraftContactId('');
      return;
    }
    setDraftContactId(matchContactByEmail(contacts, id, requesterEmail));
  }

  async function handleSave() {
    setError(null);
    try {
      await onSave(draftClientId, draftContactId);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update account link.');
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Link account" wide>
      <div className="ticket-link-account-modal">
        <p className="ticket-link-account-modal__hint">
          Changing the account clears the current contact and re-matches when possible.
        </p>

        <label className="field">
          <span className="field-label">Account</span>
          <AccountTypeahead
            clients={clients}
            value={draftClientId}
            allowNone
            noneLabel="— Unlinked —"
            onChange={handleAccountPick}
            disabled={busy}
          />
        </label>

        <label className="field">
          <span className="field-label">Contact</span>
          <select
            className="field-input"
            value={draftContactId}
            disabled={busy || !draftClientId}
            onChange={(e) => setDraftContactId(String(e.target.value))}
          >
            <option value="">{draftClientId ? 'None' : 'Select an account first'}</option>
            {contactsForClient.map((c) => (
              <option key={String(c.id)} value={String(c.id)}>
                {contactLabel(c)}
              </option>
            ))}
          </select>
        </label>

        <div className="ticket-link-account-modal__actions">
          <button type="button" className="btn-secondary btn-sm" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={busy}
            onClick={() => void handleSave()}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
        {error ? <p className="form-error">{error}</p> : null}
      </div>
    </Modal>
  );
}
